/**
 * What ACES does that school can't easily do. Framed as "ACES + school"
 * not "ACES vs school" — schools are not the enemy.
 *
 * Mobile: each row becomes a stacked card (Topic heading, then labelled rows).
 * Desktop (sm+): three-column grid as before.
 *
 * Premium polish: refined table styling, icon-decorated ACES column, scroll-reveal.
 */
import { useScrollReveal } from '@/lib/useScrollReveal';

export function SchoolComparison() {
  const ref = useScrollReveal<HTMLDivElement>({ threshold: 0.06 });

  const rows: { topic: string; school: string; aces: string; icon: React.ReactNode }[] = [
    {
      topic: 'Pace',
      school: 'Same speed for 60 students.',
      aces: 'You set the pace. Pause, replay, post the entry three times.',
      icon: <ClockIcon />,
    },
    {
      topic: 'Mistakes',
      school: 'Marked wrong, move on.',
      aces: 'A wrong journal entry triggers a hint, not a red mark. The ratio reacts on screen.',
      icon: <HintIcon />,
    },
    {
      topic: 'Examples',
      school: 'Few worked problems, mostly from the textbook.',
      aces: 'Indian-context worked example on every chapter; interactive simulations to live through.',
      icon: <BookIcon />,
    },
    {
      topic: 'When you get stuck',
      school: 'Wait for the next class, or guess.',
      aces: 'Three-level graduated hints. None tells you the answer; all get you closer.',
      icon: <BulbIcon />,
    },
    {
      topic: 'Revision',
      school: 'Re-read the chapter, hope it sticks.',
      aces: 'Interactive simulations, worked examples, practice sets — replay any concept until it clicks.',
      icon: <RepeatIcon />,
    },
  ];

  return (
    <div ref={ref}>
      <div className="rounded-card-lg border border-ink-200/80 bg-cream overflow-hidden shadow-card">
        {/* ── Desktop table (sm+) ── */}
        <div className="hidden sm:grid grid-cols-[1fr_1.5fr_1.6fr] text-sm">
          {/* Header row */}
          <div className="px-5 py-3.5 bg-ink-50 border-b border-ink-200 text-[10px] font-bold uppercase tracking-[0.12em] text-ink-500">Topic</div>
          <div className="px-5 py-3.5 bg-ink-50 border-b border-ink-200 text-[10px] font-bold uppercase tracking-[0.12em] text-ink-500">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-ink-300 inline-block" />
              In the classroom
            </span>
          </div>
          <div className="px-5 py-3.5 border-b border-accent-500/25 text-[10px] font-bold uppercase tracking-[0.12em] text-accent-600 bg-accent-50/70">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-accent-500 inline-block" />
              With ACES alongside
            </span>
          </div>
          {rows.map((r, i) => (
            <DesktopRow key={i} {...r} odd={i % 2 === 1} />
          ))}
        </div>

        {/* ── Mobile stacked cards (< sm) ── */}
        <div className="sm:hidden divide-y divide-ink-100">
          {rows.map((r, i) => (
            <MobileCard key={i} {...r} odd={i % 2 === 1} />
          ))}
        </div>
      </div>
    </div>
  );
}

function DesktopRow({
  topic, school, aces, icon, odd,
}: {
  topic: string; school: string; aces: string; icon: React.ReactNode; odd: boolean;
}) {
  const base = odd ? 'bg-ink-50/60' : 'bg-cream';
  return (
    <>
      <div className={`${base} px-5 py-4 border-t border-ink-100`}>
        <div className="flex items-center gap-2">
          <span className="text-accent-600 w-4 h-4 flex-shrink-0">{icon}</span>
          <span className="font-semibold text-ink-900 text-[0.875rem]">{topic}</span>
        </div>
      </div>
      <div className={`${base} px-5 py-4 border-t border-ink-100 text-ink-600 text-sm leading-relaxed`}>{school}</div>
      <div
        className={`${odd ? 'bg-accent-50/70' : 'bg-accent-50/40'} px-5 py-4 border-t border-ink-100 border-l border-l-accent-200 text-ink-800 text-sm leading-relaxed`}
      >
        {aces}
      </div>
    </>
  );
}

function MobileCard({
  topic, school, aces, icon, odd,
}: {
  topic: string; school: string; aces: string; icon: React.ReactNode; odd: boolean;
}) {
  return (
    <div className={`px-4 py-5 ${odd ? 'bg-ink-50/50' : 'bg-cream'}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-accent-600 w-4 h-4">{icon}</span>
        <div className="font-semibold text-ink-900 text-[0.875rem]">{topic}</div>
      </div>
      <div className="space-y-2">
        <div className="rounded-xl border border-ink-200 bg-cream px-3.5 py-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-500 mb-1">In the classroom</div>
          <div className="text-sm text-ink-500 leading-relaxed">{school}</div>
        </div>
        <div className="rounded-xl border border-accent-200/70 bg-accent-50/60 px-3.5 py-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-accent-600 mb-1">With ACES alongside</div>
          <div className="text-sm text-ink-800 leading-relaxed">{aces}</div>
        </div>
      </div>
    </div>
  );
}

// ── Small icon set ─────────────────────────────────────────────────────────────
function ClockIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <circle cx="8" cy="8" r="6" />
      <path d="M8 5v3l2 2" strokeLinecap="round" />
    </svg>
  );
}
function HintIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <circle cx="8" cy="8" r="6" />
      <path d="M8 5v4M8 11v1" strokeLinecap="round" />
    </svg>
  );
}
function BookIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <path d="M3 2h8a1 1 0 011 1v10a1 1 0 01-1 1H3V2z" />
      <path d="M7 2v12" strokeLinecap="round" />
    </svg>
  );
}
function BulbIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <path d="M6 12h4M7 14h2M8 2a4 4 0 00-2 7.46V11h4V9.46A4 4 0 008 2z" strokeLinecap="round" />
    </svg>
  );
}
function RepeatIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <path d="M2 8a6 6 0 106-6H6" strokeLinecap="round" />
      <path d="M4 4L6 2l-2-2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
